import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import DashboardLayout from '../../layouts/DashboardLayout';
import PublicLayout from '../../layouts/PublicLayout';

/**
 * PermissionRoute - Permission-based route guard (React Router 5)
 * 
 * Phase 5: M5.T5.3 - Permission Route Guards
 * 
 * Features:
 * - Check user permission before rendering route
 * - Redirect to /login if not authenticated
 * - Redirect to /403 if no permission
 * - Support for requireAny (OR logic) and requireAll (AND logic)
 * 
 * Usage:
 * <PermissionRoute path="/admin/permissions" component={PermissionsPage} permission="permissions.manage" />
 * <PermissionRoute path="/company/users" component={UsersPage} requireAny={["users.view", "users.manage"]} />
 * <PermissionRoute path="/documents/create" component={CreateDoc} requireAll={["documents.create", "templates.view"]} />
 * 
 * CRITICAL: This project uses React Router 5!
 * - Use <Route> with component prop (not element)
 * - Use <Redirect> (not Navigate)
 */
const PermissionRoute = ({ 
  component: Component, 
  permission, 
  requireAny, 
  requireAll,
  layout,
  ...rest 
}) => {
  const { user, permissions, isAuthenticated } = useSelector((state) => state.auth);
  
  return (
    <Route
      {...rest}
      render={(props) => {
        // 1. Not authenticated → redirect to login
        if (!isAuthenticated || !user) {
          return (
            <Redirect 
              to={{
                pathname: '/login',
                state: { from: props.location } // Save original location for redirect after login
              }}
            />
          );
        }
        
        // 2. Check permissions
        let hasPermission = true;
        
        if (permission) {
          // Single permission check
          hasPermission = permissions.includes(permission);
        } else if (requireAny && Array.isArray(requireAny)) {
          // OR logic: User needs at least ONE of the permissions
          hasPermission = requireAny.some(perm => permissions.includes(perm));
        } else if (requireAll && Array.isArray(requireAll)) {
          // AND logic: User needs ALL of the permissions
          hasPermission = requireAll.every(perm => permissions.includes(perm));
        } else {
          // No permission requirement specified, allow access (authenticated users only)
          hasPermission = true;
        }
        
        // 3. No permission → redirect to 403
        if (!hasPermission) {
          return <Redirect to="/403" />;
        }
        
        // 4. Has permission → render component with layout if specified
        const getLayoutComponent = () => {
          switch (layout) {
            case 'dashboard':
              return DashboardLayout;
            case 'public':
              return PublicLayout;
            case 'none':
            default:
              return null;
          }
        };
        
        const Layout = getLayoutComponent();
        const ComponentToRender = <Component {...props} />;
        
        if (Layout) {
          return <Layout>{ComponentToRender}</Layout>;
        }
        
        return ComponentToRender;
      }}
    />
  );
};

PermissionRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  permission: PropTypes.string, // Single permission
  requireAny: PropTypes.arrayOf(PropTypes.string), // OR logic (at least one)
  requireAll: PropTypes.arrayOf(PropTypes.string), // AND logic (all required)
  // Note: Only one of permission, requireAny, requireAll should be provided
};

export default PermissionRoute;

